/**
 * Generate Word List View
 *
 * Dedicated page for AI-powered word list generation.
 * Shows the prepopulated prompt before sending to the AI.
 * Can be pre-filled from an idea (from Generate Ideas page).
 */

import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { getSkillLevelEnglishName } from '../../../services/initialDataApi';
import { useGenerateWordList } from '../services/wordListsApi';
import { WordsNavigation } from '../components/WordsNavigation';
import type { WordListIdea, WordListCategoryType } from '../types/generation.types';

const LIST_TYPE_OPTIONS = [
  { value: '', label: 'Auto-detect from prompt' },
  { value: 'topics', label: 'Topics (themed vocabulary)' },
  { value: 'exams', label: 'Exams (IELTS, TOEFL, etc.)' },
  { value: 'frequency', label: 'Frequency (common words)' },
  { value: 'custom', label: 'Custom' },
] as const;

/** State passed from Generate Ideas page */
interface LocationState {
  idea?: WordListIdea;
  categoryType?: WordListCategoryType;
}

export function GenerateWordListView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state: initialDataState } = useInitialData();
  const locationState = location.state as LocationState | null;

  const languages = (initialDataState.data?.languages ?? []).filter(
    (lang) => lang.code !== 'zxx' && lang.code !== 'other'
  );
  const difficultyOptions = useMemo(() => {
    const skillLevels = initialDataState.data?.skillLevels ?? [];
    return [
      { value: '', label: 'Auto-detect from prompt' },
      ...skillLevels.map((level) => {
        const name = getSkillLevelEnglishName(level);
        return { value: name, label: name };
      }),
    ];
  }, [initialDataState.data?.skillLevels]);

  // Get language from URL params
  const languageCode = searchParams.get('lang') || languages[0]?.code || 'en';
  const languageName = languages.find((l) => l.code === languageCode)?.name || languageCode;

  // Form state
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState<number | ''>('');
  const [difficulty, setDifficulty] = useState('');
  const [listType, setListType] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [sampleWords, setSampleWords] = useState<string[]>([]);

  // Pre-fill from idea if navigated from Generate Ideas page
  useEffect(() => {
    if (locationState?.idea) {
      const idea = locationState.idea;
      setTopic(`${idea.name}\n${idea.description}`);
      setWordCount(idea.estimatedWordCount);
      setDifficulty(idea.difficulty);
      setSampleWords(idea.sampleWords);
      if (locationState.categoryType) {
        setListType(locationState.categoryType);
      }
    }
  }, [locationState]);

  const generateMutation = useGenerateWordList();

  // Build the prompt that will be sent to the AI
  const generatedPrompt = useMemo(() => {
    const parts: string[] = [];

    // Main topic/theme
    if (topic.trim()) {
      parts.push(topic.trim());
    }

    // Word count
    if (wordCount) {
      parts.push(`Target count: ${wordCount} words`);
    }

    // Difficulty level
    if (difficulty) {
      parts.push(`Difficulty: ${difficulty}`);
    }

    // List type
    if (listType) {
      parts.push(`List type: ${listType}`);
    }

    // Sample words from idea
    if (sampleWords.length > 0) {
      parts.push(`Include these seed words if appropriate: ${sampleWords.join(', ')}`);
    }

    // Additional instructions
    if (additionalInstructions.trim()) {
      parts.push(additionalInstructions.trim());
    }

    return parts.join('\n');
  }, [topic, wordCount, difficulty, listType, sampleWords, additionalInstructions]);

  const handleGenerate = async () => {
    if (!generatedPrompt.trim()) {
      toast.error('Please enter a topic or description for the word list');
      return;
    }

    try {
      const wordList = await generateMutation.mutateAsync({
        prompt: generatedPrompt,
        language_code: languageCode,
      });

      toast.success(`Created "${wordList.name}" with ${wordList.wordCount} words`);
      navigate(`/words/lists/${wordList.id}/words`);
    } catch (error) {
      toast.error(`Failed to generate word list: ${(error as Error).message}`);
    }
  };

  const isGenerating = generateMutation.isPending;
  const canGenerate = topic.trim().length > 0 && !isGenerating;

  return (
    <div className="p-6">
      <WordsNavigation />

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Generate Word List</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h2>

          <div className="space-y-4">
            {/* Language (read-only, from URL) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {languageName}
              </div>
              <p className="mt-1 text-xs text-gray-500">Change language using the dropdown above</p>
            </div>

            {/* Topic / Theme */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topic / Theme <span className="text-red-500">*</span>
              </label>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                rows={3}
                placeholder="e.g., Kitchen vocabulary, Airport and travel, Business meetings"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGenerating}
              />
            </div>

            {/* Sample Words (from idea) */}
            {sampleWords.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seed Words (from idea)
                </label>
                <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {sampleWords.map((word, i) => (
                    <span
                      key={i}
                      className="inline-flex px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                    >
                      {word}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setSampleWords([])}
                  className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear seed words
                </button>
              </div>
            )}

            {/* Word Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Word Count
              </label>
              <input
                type="number"
                value={wordCount}
                onChange={(e) => setWordCount(e.target.value ? Number(e.target.value) : '')}
                placeholder="e.g., 30 (leave empty for ~30 words)"
                min={10}
                max={70}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGenerating}
              />
              <p className="mt-1 text-xs text-gray-500">Default ~30 words, soft cap of 70 words</p>
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty Level
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGenerating}
              >
                {difficultyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* List Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">List Type</label>
              <select
                value={listType}
                onChange={(e) => setListType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGenerating}
              >
                {LIST_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Additional Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Instructions
              </label>
              <textarea
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                rows={3}
                placeholder="Any specific requirements, words to include/exclude, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isGenerating}
              />
            </div>
          </div>
        </div>

        {/* Prompt Preview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Prompt Preview</h2>
          <p className="text-sm text-gray-500 mb-3">
            This is the prompt that will be sent to the AI for word list generation:
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-4 min-h-[200px]">
            {generatedPrompt ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                {generatedPrompt}
              </pre>
            ) : (
              <p className="text-gray-400 italic">Enter a topic to see the generated prompt...</p>
            )}
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>What the AI will generate:</strong>
            </p>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li>Word list name and description</li>
              <li>Words with example sentences in {languageName}</li>
              <li>Translations to all supported languages</li>
              <li>Images for each word</li>
              <li>Pronunciation audio for each word</li>
            </ul>
          </div>

          <div className="mt-6">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isGenerating ? 'Generating Word List...' : 'Generate Word List'}
            </button>
          </div>
        </div>
      </div>

      {/* Generation Progress Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Word List</h3>
            <p className="text-gray-500 text-sm">
              Creating words with translations, example sentences, and images. This may take several
              minutes...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
